import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ChevronLeft, Sparkles, HelpCircle, ShieldCheck } from 'lucide-react';
import articlesData from '../../data/articles.json';

const cleanNorm = (str) => {
  return String(str || '')
    .replace(/[أإآ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .replace(/[\u0660-\u0669]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x0660 + 48))
    .replace(/طريق\s+/g, '')
    .replace(/[-_\s]+/g, ' ')
    .trim();
};

const isAreaMatch = (target, query) => {
  const t = cleanNorm(target);
  const q = cleanNorm(query);
  if (!t || !q) return false;
  if (t === q) return true;
  if (t === q.replace(/^ال/, '') || q === t.replace(/^ال/, '')) return true;
  if (t.replace(/\s+/g, '') === q.replace(/\s+/g, '')) return true;
  return false;
};

const AreaRelatedArticles = ({ areaName }) => {
  // 1. Find both dedicated articles specifically written for this area
  const dedicatedArticles = useMemo(() => {
    if (!areaName || !articlesData || !articlesData.articles) return [];

    const matched = articlesData.articles.filter((art) => {
      return art.targetArea && isAreaMatch(art.targetArea, areaName);
    });

    const faqArticle = matched.find(
      (a) => a.slug.startsWith('faq-winch-') || a.title.includes('أسئلة') || a.title.includes('اسئلة')
    );
    const guideArticle = matched.find((a) => a !== faqArticle);

    const result = [];
    if (guideArticle) result.push({ ...guideArticle, isFaq: false });
    if (faqArticle) result.push({ ...faqArticle, isFaq: true });

    // Fallback if matched doesn't distinguish but has articles
    matched.forEach((a) => {
      if (result.length < 2 && !result.some((r) => r.id === a.id)) {
        result.push({ ...a, isFaq: a.slug.startsWith('faq-winch-') });
      }
    });

    return result;
  }, [areaName]);

  // 2. Find general complementary pillar guides (excluding dedicated ones)
  const complementaryArticles = useMemo(() => {
    if (!articlesData || !articlesData.articles) return [];

    const fallbackSlugs = [
      'اسعار-ونش-انقاذ-2026',
      'safety-procedures-car-breakdown',
      'how-to-choose-best-tow-service',
      'roadside-assistance-complete-guide'
    ];

    const dedicatedIds = dedicatedArticles.map((a) => a.id);

    return articlesData.articles
      .filter((art) => !dedicatedIds.includes(art.id) && fallbackSlugs.includes(art.slug))
      .slice(0, 2);
  }, [dedicatedArticles]);

  if (dedicatedArticles.length === 0 && complementaryArticles.length === 0) return null;

  return (
    <aside
      className="area-related-articles"
      aria-labelledby="related-articles-heading"
      style={{
        background: '#ffffff',
        borderRadius: '1.5rem',
        padding: '2rem',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        marginBottom: '2rem',
        border: '2px solid #e3f0ff',
        direction: 'rtl'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.8rem' }}>
        <BookOpen size={26} color="#0077ff" />
        <h3 id="related-articles-heading" style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0a2c61', margin: 0 }}>
          المقالات والأدلة الإرشادية الداعمة لـ {areaName}
        </h3>
      </div>
      <p style={{ color: '#64748b', fontSize: '1.02rem', margin: '0 0 1.5rem 0', lineHeight: 1.6 }}>
        اطلع على الأدلة الميدانية المخصصة لمنطقة {areaName} وأهم الأسئلة الشائعة حول أسعار الإنقاذ وسرعة الوصول لضمان قيادة آمنة:
      </p>

      {/* 1. Featured Dedicated Supporting Articles (2 Cards Grid) */}
      {dedicatedArticles.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '1.5rem',
            marginBottom: '1.8rem'
          }}
        >
          {dedicatedArticles.map((art) => (
            <article
              key={art.id}
              style={{
                background: art.isFaq
                  ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
                  : 'linear-gradient(135deg, #f0f7ff 0%, #e3f0ff 100%)',
                border: art.isFaq ? '2px solid #22c55e' : '2px solid #0077ff',
                borderRadius: '1.2rem',
                padding: '1.5rem',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                boxShadow: art.isFaq
                  ? '0 4px 15px rgba(34, 197, 94, 0.08)'
                  : '0 4px 15px rgba(0, 119, 255, 0.08)'
              }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.8rem' }}>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      background: art.isFaq ? '#16a34a' : '#0077ff',
                      color: '#ffffff',
                      padding: '0.35rem 0.9rem',
                      borderRadius: '2rem',
                      fontSize: '0.85rem',
                      fontWeight: 800
                    }}
                  >
                    {art.isFaq ? <HelpCircle size={15} /> : <ShieldCheck size={15} />}
                    {art.isFaq
                      ? `دليل الأسئلة والأسعار في ${areaName}`
                      : `الدليل الميداني وطوارئ السيارات في ${areaName}`}
                  </span>
                </div>
                <h4
                  style={{
                    fontSize: '1.2rem',
                    fontWeight: 800,
                    color: '#0a2c61',
                    margin: '0 0 0.8rem 0',
                    lineHeight: 1.5
                  }}
                >
                  {art.title}
                </h4>
                <p
                  style={{
                    fontSize: '0.95rem',
                    color: '#334155',
                    lineHeight: 1.7,
                    margin: '0 0 1.2rem 0'
                  }}
                >
                  {art.summary}
                </p>
              </div>

              <Link
                to={`/articles/${art.slug}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  background: art.isFaq ? '#15803d' : '#0a2c61',
                  color: '#ffffff',
                  padding: '0.7rem 1.5rem',
                  borderRadius: '2rem',
                  fontWeight: 800,
                  fontSize: '0.95rem',
                  textDecoration: 'none',
                  boxShadow: art.isFaq
                    ? '0 4px 12px rgba(21,128,61,0.25)'
                    : '0 4px 12px rgba(10,44,97,0.2)',
                  transition: 'background 0.2s ease, transform 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = art.isFaq ? '#16a34a' : '#0077ff';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = art.isFaq ? '#15803d' : '#0a2c61';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <span>
                  {art.isFaq
                    ? `استعراض إجابات الأسئلة والأسعار لـ ${areaName}`
                    : `قراءة الدليل الميداني لـ ${areaName} بالكامل`}
                </span>
                <ChevronLeft size={18} />
              </Link>
            </article>
          ))}
        </div>
      )}

      {/* 2. Complementary Pillar Articles Grid */}
      {complementaryArticles.length > 0 && (
        <div>
          <h4
            style={{
              fontSize: '1.05rem',
              fontWeight: 700,
              color: '#475569',
              marginBottom: '0.8rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}
          >
            <Sparkles size={16} color="#0077ff" />
            أدلة وإرشادات عامة مكملة تهمك:
          </h4>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1rem'
            }}
          >
            {complementaryArticles.map((art) => (
              <Link
                key={art.id}
                to={`/articles/${art.slug}`}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '1rem',
                  padding: '1.2rem',
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'all 0.25s ease',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.borderColor = '#0077ff';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,119,255,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                }}
              >
                <div>
                  <h5
                    style={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#0a2c61',
                      margin: '0 0 0.5rem 0',
                      lineHeight: 1.5
                    }}
                  >
                    {art.title}
                  </h5>
                  <p
                    style={{
                      fontSize: '0.9rem',
                      color: '#475569',
                      margin: '0 0 0.8rem 0',
                      lineHeight: 1.6,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {art.summary}
                  </p>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: '0.6rem',
                    borderTop: '1px solid #e2e8f0',
                    fontSize: '0.85rem',
                    color: '#0077ff',
                    fontWeight: 700
                  }}
                >
                  <span>قراءة الدليل</span>
                  <ChevronLeft size={16} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
};

export default AreaRelatedArticles;
